import { faker } from '@faker-js/faker';

export type CertificateStandardScope = 'product' | 'facility';

export type CertificateStandardRecord = {
  id: string;
  /** Numeric id aligned with Mirage/SCM list items (stable; not the map index when filtered by scope). */
  apiId: number;
  label: string;
  scope: CertificateStandardScope;
};

/** Deterministic mock size + seed so lists are stable across runs and tests. */
const CERTIFICATE_STANDARD_MOCK_COUNT = 110;
const CERTIFICATE_STANDARD_FAKER_SEED = 202_604_22;

/** Builds a stable kebab-case id from a label; use one `used` Set per generated list for uniqueness. */
export function stableCertificateId(label: string, index: number, used: Set<string>): string {
  let base = label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 88);
  if (!base) base = `cert-${index}`;
  let id = base;
  let suffix = 0;
  while (used.has(id)) {
    suffix += 1;
    id = `${base}-${suffix}`;
  }
  used.add(id);
  return id;
}

faker.seed(CERTIFICATE_STANDARD_FAKER_SEED);

function makeStandardLabel(): string {
  const suffix = faker.helpers.maybe(
    () => ` ${faker.helpers.arrayElement(['Level 1', 'Level 2', 'Level 3', 'Module', 'Plus'])}`,
    { probability: 0.35 }
  );
  return `${faker.commerce.productAdjective()} ${faker.helpers.arrayElement([
    'Standard',
    'Certification',
    'Conformance',
    'Compliance Program',
    'Chain of Custody',
    'Verified Status',
    'Approved Label',
  ])}${suffix ?? ''}`.trim();
}

function generateCertificateStandardRecords(): CertificateStandardRecord[] {
  const usedIds = new Set<string>();
  const rows: CertificateStandardRecord[] = [];

  const scopes: CertificateStandardScope[] = ['product', 'facility'];

  for (let i = 0; i < CERTIFICATE_STANDARD_MOCK_COUNT; i++) {
    const scope = faker.helpers.arrayElement(scopes);
    const label = makeStandardLabel();
    const id = stableCertificateId(label, i, usedIds);
    rows.push({ id, apiId: i + 1, label, scope });
  }

  return rows;
}

export const CERTIFICATE_STANDARD_RECORDS = generateCertificateStandardRecords();

export function getCertificateStandardsForScope(
  scope: CertificateStandardScope
): CertificateStandardRecord[] {
  return CERTIFICATE_STANDARD_RECORDS.filter(r => r.scope === scope);
}
